import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { BillingSummary } from '@/billing/api/FetchBillingList';
import { BillingNoteTemplatePicker } from '@/billing/component/BillingNoteTemplatePicker';
import { useAddBillingNote } from '@/billing/hooks/useAddBillingNote';
import { billingNoteInputSchema } from '@/billing/types/BillingNoteInput';
import { Button } from '@/common/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@/common/components/ui/form';

export type CreateBillingNoteFormProps = {
  billing: BillingSummary;
};

const resolver = zodResolver(z.object({ note: billingNoteInputSchema }));

const CreateBillingNoteForm = ({ billing }: CreateBillingNoteFormProps) => {
  const { t } = useTranslation();
  const { addNote, isPending } = useAddBillingNote();

  const form = useForm({ resolver, mode: 'onChange' });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(({ note }) =>
          addNote(
            { billingUuid: billing.uuid, note },
            {
              onSuccess: () => {
                toast.success(t('billing.drawer.noteSaved'));
                form.reset();
              },
              onError: () => toast.error(t('billing.drawer.noteSaveError')),
            },
          ),
        )}
        className="bg-primary-foreground shrink-0 p-4 space-y-2 border-t border-sidebar-border"
      >
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <BillingNoteTemplatePicker {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="w-full"
          disabled={!form.formState.isValid || isPending}
        >
          {t('actions.confirm')}
        </Button>

        <p className="text-center text-xxs text-muted-foreground">
          {t('billing.notesHistory.authorOnly')}
        </p>
      </form>
    </Form>
  );
};

export default CreateBillingNoteForm;
